import { useRef, type FC, useEffect } from 'react';
import VerificationCode from 'react-verification-code-input';

import { Error } from '@livechat/design-system-icons';
import { Icon, Text } from '@livechat/design-system-react-components';

import { useVerifyAccount } from 'hooks/use-verify-account';

import * as styles from './styles';

type Props = {
  email: string;
  isAccountWhitelisted: boolean;
  onVerificationWithoutProductCompleted(): void;
};

export const VerificationCodeField: FC<Props> = ({
  isAccountWhitelisted,
  email,
  onVerificationWithoutProductCompleted,
}) => {
  const inputRef = useRef(null);
  const {
    error: verificationError,
    handleVerify: onVerify,
    clearError,
    isProcessing,
  } = useVerifyAccount(isAccountWhitelisted, email, onVerificationWithoutProductCompleted);

  const handleVerify = (code: string): void => {
    onVerify(code);
  };

  const handleVerificationCodeChange = (value: string) => {
    if (verificationError && value.length < 6) {
      clearError();
    }
  };

  useEffect(() => {
    if (!isProcessing && verificationError) {
      (inputRef?.current as any)?.iRefs[5].current.focus();
    }
  }, [isProcessing, verificationError]);

  // Focus the verification code field whenever the window regains focus (user returns to the tab / app)
  useEffect(() => {
    const handleWindowFocus = () => {
      if (isProcessing) {
        return;
      }

      const firstInput = (inputRef?.current as any)?.iRefs[0];
      if (firstInput?.current.value === '') {
        firstInput?.current.focus();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [isProcessing]);

  return (
    <div css={styles.container}>
      <form autoComplete="off">
        <VerificationCode
          ref={inputRef}
          css={styles.verificationCode}
          onComplete={handleVerify}
          onChange={handleVerificationCodeChange}
          loading={isProcessing}
          disabled={isProcessing}
          type="number"
          fields={6}
          fieldWidth={36}
          fieldHeight={54}
          autoFocus
        />
      </form>
      {verificationError && (
        <div css={styles.errorContainer}>
          <Icon source={Error} kind="error" size="small" />
          <Text css={styles.error} size="sm">
            {verificationError}
          </Text>
        </div>
      )}
    </div>
  );
};
